import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { BOOKING_URL } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-navyline bg-ink/70 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 transition-colors duration-200 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

export default function ConsultationForm() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", story: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        phone: form.phone || null,
        current_website: null,
        business_type: null,
        goals: [],
        current_stage: null,
        investment_range: null,
        source: "contact-form",
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-confirmation" className="rounded-3xl border border-gold/40 bg-navycard p-10 text-center sm:p-14">
        <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.4} />
        <h2 className="mt-6 font-display text-2xl font-bold text-white sm:text-3xl">Message Received</h2>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">
          Thank you for reaching out to AT Digital. We'll reply within one business day. Want to talk sooner? You can
          book a time right now.
        </p>
        <div className="mt-9 flex flex-col items-center gap-4">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="confirmation-book-call-btn"
            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink shadow-lg shadow-gold/20 transition-colors duration-300 hover:bg-goldlight"
          >
            <CalendarCheck className="h-4 w-4" />
            Book Your Consultation Call Now
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <button
            data-testid="contact-submit-another-btn"
            onClick={() => setSubmitted(false)}
            className="text-sm font-medium text-slate-500 underline-offset-4 transition-colors duration-200 hover:text-gold hover:underline"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid="consultation-form"
      onSubmit={onSubmit}
      className="rounded-3xl border border-navyline bg-navycard p-8 sm:p-10"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <input
          data-testid="contact-name-input"
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Name"
          aria-label="Name"
          className={inputCls}
        />
        <input
          data-testid="contact-company-input"
          required
          value={form.company}
          onChange={set("company")}
          placeholder="Company"
          aria-label="Company"
          className={inputCls}
        />
        <input
          data-testid="contact-email-input"
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="Email"
          aria-label="Email"
          className={inputCls}
        />
        <input
          data-testid="contact-phone-input"
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          placeholder="Phone (optional)"
          aria-label="Phone (optional)"
          className={inputCls}
        />
        <div className="sm:col-span-2">
          <textarea
            data-testid="contact-story-input"
            required
            rows={5}
            value={form.story}
            onChange={set("story")}
            placeholder="Tell us where your business is today, where you want it to go, and what you believe is getting in the way."
            aria-label="Tell us your story"
            className={`${inputCls} resize-y`}
          />
        </div>
      </div>
      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={submitting}
        className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink transition-colors duration-200 hover:bg-goldlight disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Message
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  );
}
